import jsPDF from 'jspdf';
import CertificateHelper from './API/BackendHelper/certificate';

/**
 * Professional Certificate PDF Generator
 * Generates a beautifully designed certificate with the provided data
 * Matches the exact design with dynamic logos, signatures, and data
 */

// Color palette
const COLORS = {
  primary: [0, 75, 155],       // Blue for CERTIFICATE text
  secondary: [234, 179, 8],    // Gold
  accent: [220, 38, 38],       // Red accent
  text: [31, 41, 55],          // Dark gray
  lightText: [107, 114, 128],  // Light gray
  border: [0, 75, 155],        // Border blue
  background: [255, 255, 255], // White background
  blueGradient: [59, 130, 246], // Light blue for decorative waves
};

/**
 * Load image from URL and convert to base64
 */
const loadImageAsBase64 = async (url) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to load image:', error);
    return null;
  }
};

/**
 * Draw award badge at top (using badge.png image)
 */
const drawAwardBadge = async (doc, centerX) => {
  try {
    // Load badge from local public assets
    const badgePath = '/badge.png';
    const badgeImg = await loadImageAsBase64(window.location.origin + badgePath);
    if (badgeImg) {
      // Display badge at top center - original size
      const badgeSize = 32; // 20mm width/height (original size)
      doc.addImage(badgeImg, 'PNG', centerX - badgeSize/2, 15, badgeSize, badgeSize);
    }
  } catch (error) {
    console.log('Failed to load badge.png, skipping badge');
  }
};

/**
 * Draw decorative vectors at bottom using PNG images
 */
const drawBottomVectors = async (doc, width, height) => {
  try {
    // Load bottom left vector
    const bottomLeftPath = '/bottomLeftVector.png';
    const bottomLeftImg = await loadImageAsBase64(window.location.origin + bottomLeftPath);
    if (bottomLeftImg) {
      // Position at bottom left corner
      doc.addImage(bottomLeftImg, 'PNG', 0, height - 80, 100, 80);
    }
  } catch (error) {
    console.log('Failed to load bottomLeftVector.png');
  }
  
  try {
    // Load bottom right vector
    const bottomRightPath = '/bottomRightVector.png';
    const bottomRightImg = await loadImageAsBase64(window.location.origin + bottomRightPath);
    if (bottomRightImg) {
      // Position at bottom right corner
      doc.addImage(bottomRightImg, 'PNG', width - 100, height - 80, 100, 80);
    }
  } catch (error) {
    console.log('Failed to load bottomRightVector.png');
  }
};
/**
 * Format date to readable string
 */
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return date.toLocaleDateString('en-US', options);
};

/**
 * Generate Certificate PDF
 * @param {Object} certificateData - Certificate data from API
 * @returns {jsPDF} - Generated PDF document
 */
export const generateCertificatePDF = async (certificateData) => {
  // Create new PDF document (landscape A4)
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });
  
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  const centerX = width / 2;
  
  // Background - Add background.png as full page background
  try {
    const backgroundPath = '/background.png';
    const backgroundImg = await loadImageAsBase64(window.location.origin + backgroundPath);
    if (backgroundImg) {
      // Cover entire page with background image
      doc.addImage(backgroundImg, 'PNG', 0, 0, width, height);
    } else {
      // Fallback: white background
      doc.setFillColor(255, 255, 255);
      doc.rect(0, 0, width, height, 'F');
    }
  } catch (error) {
    console.log('Failed to load background.png, using white background');
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, width, height, 'F');
  }
  
  // Draw decorative bottom vectors
  await drawBottomVectors(doc, width, height);
  
  // Draw award badge at top center
  await drawAwardBadge(doc, centerX);
  
  // Add University logo (top left)
  if (certificateData.university_logo_url) {
    try {
      const universityLogoImg = await loadImageAsBase64(certificateData.university_logo_url);
      if (universityLogoImg) {
        doc.addImage(universityLogoImg, 'PNG', 15, 12, 30, 30);
      }
    } catch (error) {
      console.log('Failed to load university logo');
    }
  }
  
  // Add College logo (top right)
  if (certificateData.college_logo_url) {
    try {
      const collegeLogoImg = await loadImageAsBase64(certificateData.college_logo_url);
      if (collegeLogoImg) {
        doc.addImage(collegeLogoImg, 'PNG', width - 45, 12, 30, 30);
      }
    } catch (error) {
      console.log('Failed to load college logo');
    }
  }
  
  // Certificate title - "CERTIFICATE"
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(50);
  doc.setTextColor(0, 75, 155);
  doc.text('CERTIFICATE', centerX, 70, { align: 'center' });
  
  // Subtitle - "OF COMPLETION"
  doc.setFontSize(16);
  doc.setTextColor(0, 75, 155);
  doc.text('OF COMPLETION', centerX, 82, { align: 'center' });
  
  // "This certificate is proudly presented to" text
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(50, 50, 50);
  doc.text('This certificate is proudly presented to', centerX, 95, { align: 'center' });
  
  // Student name
  const studentName = certificateData.student_name || 'Student Name';
  doc.setFont('times', 'bold');
  doc.setFontSize(36);
  doc.setTextColor(0, 0, 0);
  doc.text(studentName, centerX, 110, { align: 'center' });
  
  // Dotted underline for name
  const nameWidth = doc.getTextWidth(studentName);
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.setLineDash([1, 1]);
  doc.line(centerX - nameWidth/2 - 5, 112, centerX + nameWidth/2 + 5, 112);
  doc.setLineDash([]); // Reset to solid line
  
  // Build the completion text with dynamic data
  const parentName = certificateData.parent_name || 'Parent Name';
  const courseName = certificateData.course_name || 'Course Name';
  
  // Line 1: "Son/Daughter of [Parent Name]"
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.setTextColor(50, 50, 50);
  let textY = 125;
  
  const line1Text = `Son/Daughter of `;
  const line1Width = doc.getTextWidth(line1Text);
  doc.text(line1Text, centerX - (line1Width + doc.getTextWidth(parentName)) / 2, textY);
  
  // Parent name - bold with dotted underline
  doc.setFont('helvetica', 'bold');
  const parentNameX = centerX - (line1Width + doc.getTextWidth(parentName)) / 2 + line1Width;
  doc.text(parentName, parentNameX, textY);
  const parentNameWidth = doc.getTextWidth(parentName);
  doc.setLineDash([1, 1]);
  doc.line(parentNameX, textY + 1, parentNameX + parentNameWidth, textY + 1);
  doc.setLineDash([]);
  
  // Line 2: "has successfully completed the Vocational Course on"
  textY += 8;
  doc.setFont('helvetica', 'normal');
  doc.text(`has successfully completed the Vocational Course on`, centerX, textY, { align: 'center' });
  
  // Line 3: Course name - bold with dotted underline (centered on new line)
  textY += 8;
  doc.setFont('helvetica', 'bold');
  doc.text(courseName, centerX, textY, { align: 'center' });
  const courseNameWidth = doc.getTextWidth(courseName);
  doc.setLineDash([1, 1]);
  doc.line(centerX - courseNameWidth/2, textY + 1, centerX + courseNameWidth/2, textY + 1);
  doc.setLineDash([]);
  
  // Signature section - moved down by 10%
  const signatureY = height - 40.5; // Was 45, now 40.5 (10% down = 4.5mm down)
  
  // Left signature - Principal
  if (certificateData.principal_signature_url) {
    try {
      const principalSig = await loadImageAsBase64(certificateData.principal_signature_url);
      if (principalSig) {
        doc.addImage(principalSig, 'PNG', 50, signatureY - 20, 40, 15);
      }
    } catch (error) {
      // Fallback: show placeholder text
      doc.setFont('brush script mt', 'italic');
      doc.setFontSize(14);
      doc.setTextColor(150, 150, 150);
      doc.text('Signature', 70, signatureY - 8, { align: 'center' });
    }
  } else {
    // Placeholder signature
    doc.setFont('brush script mt', 'italic');
    doc.setFontSize(14);
    doc.setTextColor(150, 150, 150);
    doc.text('Signature', 70, signatureY - 8, { align: 'center' });
  }
  
  // Principal line and name
  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.3);
  doc.line(40, signatureY, 100, signatureY);
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  const principalName = certificateData.principal_name || 'Principal Name';
  doc.text(principalName, 70, signatureY + 5, { align: 'center' });
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  const collegeName = certificateData.college_name || 'College Name';
  doc.text(`Principal, ${collegeName}`, 70, signatureY + 10, { align: 'center' });
  
  // Right signature - Director
  if (certificateData.director_signature_url) {
    try {
      const directorSig = await loadImageAsBase64(certificateData.director_signature_url);
      if (directorSig) {
        doc.addImage(directorSig, 'PNG', width - 90, signatureY - 12, 40, 15);
      }
    } catch (error) {
      doc.setFont('brush script mt', 'italic');
      doc.setFontSize(14);
      doc.setTextColor(150, 150, 150);
      doc.text('Signature', width - 70, signatureY - 8, { align: 'center' });
    }
  } else {
    doc.setFont('brush script mt', 'italic');
    doc.setFontSize(14);
    doc.setTextColor(150, 150, 150);
    doc.text('Signature', width - 70, signatureY - 8, { align: 'center' });
  }
  
  // Director line and name
  doc.line(width - 100, signatureY, width - 40, signatureY);
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  const directorName ='Mani Goel';
  doc.text(directorName, width - 70, signatureY + 5, { align: 'center' });
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(80, 80, 80);
  doc.text('Director, GyanSathi', width - 70, signatureY + 10, { align: 'center' });
  
  // Certificate Number (bottom left)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  const certNumber = certificateData.certificate_number || 'XXXXXXXXXXXXX';
  doc.text(`Certificate No.: ${certNumber}`, 15, height - 15);
  
  // D.O.I. (Date of Issue) (bottom left)
  const issueDate = formatDate(certificateData.issue_date || certificateData.issued_date);
  doc.text(`D.O.I.: ${issueDate}`, 15, height - 10);

  // Verification contact (bottom center)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(0, 0, 0);
  doc.text('To verify the credibility, contact: info@gyansathi.com', centerX, height - 6, { align: 'center' });
  
  return doc;
};

/**
 * Download certificate as PDF
 * @param {Object} certificateData - Certificate data from API
 */
export const downloadCertificate = async (certificateData) => {
  const doc = await generateCertificatePDF(certificateData);
  const fileName = `Certificate-${certificateData.student_name || 'Student'}-${certificateData.certificate_number || 'download'}.pdf`;
  doc.save(fileName);
};

/**
 * Open certificate in new tab
 * @param {Object} certificateData - Certificate data from API
 */
export const openCertificateInNewTab = async (certificateData) => {
  const doc = await generateCertificatePDF(certificateData);
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfUrl, '_blank');
};

/**
 * Fetch certificate from API and download as PDF
 * @param {string} certificateId - Certificate ID
 * @param {boolean} usePublicEndpoint - Whether to use public endpoint (default: false)
 */
export const fetchAndDownloadCertificate = async (certificateId, usePublicEndpoint = false) => {
  try {
    const certificateData = await CertificateHelper.fetchCertificateForPDF(certificateId, usePublicEndpoint);
    await downloadCertificate(certificateData);
  } catch (error) {
    throw new Error(`Failed to download certificate: ${error.message}`);
  }
};

/**
 * Fetch certificate from API and open in new tab
 * @param {string} certificateId - Certificate ID
 * @param {boolean} usePublicEndpoint - Whether to use public endpoint (default: false)
 */
export const fetchAndOpenCertificate = async (certificateId, usePublicEndpoint = false) => {
  try {
    const certificateData = await CertificateHelper.fetchCertificateForPDF(certificateId, usePublicEndpoint);
    await openCertificateInNewTab(certificateData);
  } catch (error) {
    throw new Error(`Failed to open certificate: ${error.message}`);
  }
};

export default {
  generateCertificatePDF,
  downloadCertificate,
  openCertificateInNewTab,
  fetchAndDownloadCertificate,
  fetchAndOpenCertificate,
};
